import ROUTES from "@constants/routing";
import { useKeycloakAdmin } from "@hooks/useKeycloakRoles";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import ScienceIcon from "@mui/icons-material/Science";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme
} from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const AppBarMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isAdministrator = useKeycloakAdmin();
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  const handleCloseLogoutModal = () => {
    setOpenLogoutModal(false);
  };

  const onLogout = () => {
    setOpenLogoutModal(true);
  };

  const handleLogout = () => {
    setIsOpen(false);
    setOpenLogoutModal(false);
    keycloak.logout();
  };
  const toggleDrawer = (open: boolean) => () => {
    setIsOpen(open);
  };

  const handleNavigation = (path: string) => () => {
    navigate(path);
    setIsOpen(false);
  };

  const menuItems = useMemo(
    () => [
      {
        text: "Home",
        icon: <HomeIcon />,
        path: ROUTES.HOME,
        show: true,
        active: location.pathname === ROUTES.HOME
      },
      {
        text: t("navbar.tabs.mmm_model"),
        icon: (
          <ScienceIcon
            sx={{
              color: location.pathname.includes(ROUTES.MMM_MODEL)
                ? theme.palette.primary.contrastText
                : theme.palette.primary.main
            }}
          />
        ),
        path: ROUTES.MMM_MODEL,
        show: true,
        active: location.pathname.includes(ROUTES.MMM_MODEL)
      },
      {
        text: t("navbar.tabs.simulator"),
        icon: (
          <AssessmentIcon
            sx={{
              color: location.pathname.includes(ROUTES.SIMULATOR)
                ? theme.palette.primary.contrastText
                : theme.palette.primary.main
            }}
          />
        ),
        path: ROUTES.SIMULATOR,
        show: true,
        active: location.pathname.includes(ROUTES.SIMULATOR)
      },
      {
        text: t("navbar.admin_panel"),
        icon: (
          <AdminPanelSettingsIcon
            sx={{
              color: location.pathname.includes(ROUTES.ADMIN_PANEL)
                ? theme.palette.primary.contrastText
                : theme.palette.success.main
            }}
          />
        ),
        path: ROUTES.ADMIN_PANEL,
        show: isAdministrator,
        active: location.pathname.includes(ROUTES.ADMIN_PANEL)
      }
    ],
    [t, isAdministrator, location.pathname]
  );

  return (
    <Box>
      <IconButton
        size="large"
        edge="start"
        color="inherit"
        aria-label="menu"
        onClick={toggleDrawer(true)}
      >
        <MenuIcon />
      </IconButton>

      <Drawer anchor="left" open={isOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 250 }} role="presentation">
          <List>
            {menuItems
              .filter((item) => item.show)
              .map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={handleNavigation(item.path)}
                    selected={item.active}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover
                        }
                      }
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            <ListItem key={"logout"} disablePadding>
              <ListItemButton onClick={onLogout}>
                <ListItemIcon>
                  <LogoutIcon color="error" />
                </ListItemIcon>
                <ListItemText primary={t("navbar.logout_")} />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
        </Box>
        <Dialog
          open={openLogoutModal}
          onClose={handleCloseLogoutModal}
          aria-labelledby="logout-dialog-title"
        >
          <DialogTitle id="logout-dialog-title">
            {t("navbar.logout.title")}
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseLogoutModal} variant="outlined">
              {t("navbar.logout.cancel")}
            </Button>
            <Button
              onClick={handleLogout}
              autoFocus
              variant="contained"
              color="error"
            >
              {t("navbar.logout.confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      </Drawer>
    </Box>
  );
};

export default AppBarMenu;
